// Bulk / Sheet "deliver without request" rows (IPD-397): one row per request plus its delivery columns.
import { format } from "date-fns";
import {
  buildDeliveryAdName,
  buildRequestRemarks,
  sanitizeToken,
} from "../../../utils/adNameBuilder";
import { validateAssetUrl } from "../../AdDeliver/utils/validateAssetUrl";

export const HOOKS_MAX = 10;

// Request columns that direct rows drop from the brief: due date is today, hooks move next to the links.
export const DIRECT_EXCLUDED_KEYS = new Set([
  "hooks_count",
  "priority",
  "due_delivery_date",
]);

// Request columns that are required on a normal request but optional when delivering directly.
export const DIRECT_OPTIONAL_KEYS = new Set(["script"]);

/** AI ads have no creator, so creator is only required for Human ones. */
export const creatorRequired = (humanAi) =>
  (humanAi || "").trim().toLowerCase() !== "ai";

// One column per hook; the number of filled links is the number of hooks.
export const HOOK_LINK_KEYS = Array.from(
  { length: HOOKS_MAX },
  (_, i) => `hook_${i + 1}_link`
);

// Same order as DIRECT_SHEET_DELIVERY_KEYS on the backend, so a pasted sheet lines up.
export const DIRECT_DELIVERY_COLUMNS = [
  ["requester_name", "Requester *"],
  ["ad_type", "Ad type *"],
  ["creator_name", "Creator"],
  ["editor_name", "Editor *"],
  ["hooks_count", "Hooks"],
  ...HOOK_LINK_KEYS.map((k, i) => [k, `Hook ${i + 1} link *`]),
];
export const DIRECT_DELIVERY_KEYS = DIRECT_DELIVERY_COLUMNS.map(([k]) => k);

/** Request keys/labels for the direct grid: drop the excluded ones, append the delivery columns. */
export function directColumns(keys, labels) {
  const kept = keys
    .map((k, i) => [k, DIRECT_OPTIONAL_KEYS.has(k) ? labels[i].replace(" *", "") : labels[i]])
    .filter(([k]) => !DIRECT_EXCLUDED_KEYS.has(k));
  const all = [...kept, ...DIRECT_DELIVERY_COLUMNS];
  return { keys: all.map(([k]) => k), labels: all.map(([, l]) => l) };
}

/** A grid row's ad set name; direct rows preview the final name with agency, creator, editor and today's date. */
export const handleGridAdsetName = ({ row, agencyName = "", direct = false }) => {
  const fields = {
    track: row.track,
    track_number: row.track_number,
    topic: row.topic,
    script_name: row.script_name,
    module_type: row.module_type,
    module: row.module,
    creative_format: row.creative_format,
    visual_format: row.visual_format,
    human_ai: row.human_ai,
    agency_name: agencyName,
    script_type: row.script_type,
    requester_name: direct ? row.requester_name : undefined,
  };
  if (!direct) return buildRequestRemarks(fields);
  return buildDeliveryAdName(
    buildRequestRemarks({
      ...fields,
      delivery_date: format(new Date(), "ddMMMyy"),
    }),
    { creator_name: row.creator_name, editor_name: row.editor_name }
  );
};

/** Hooks the row asked for; blank means 1, like a normal request. */
export const hooksOf = (row) =>
  Math.min(HOOKS_MAX, Math.max(1, parseInt(row?.hooks_count, 10) || 1));

const hookLinks = (row) =>
  HOOK_LINK_KEYS.slice(0, hooksOf(row)).map((k) => String(row[k] ?? "").trim());

const hookNumber = (key) =>
  HOOK_LINK_KEYS.includes(key) ? HOOK_LINK_KEYS.indexOf(key) + 1 : 0;

/** Show hook link columns up to the largest hook count in the grid. */
export function visibleColumns(keys, labels, rows) {
  const most = Math.max(1, ...rows.map(hooksOf));
  const shown = keys
    .map((k, i) => [k, labels[i]])
    .filter(([k]) => hookNumber(k) <= most);
  return { keys: shown.map(([k]) => k), labels: shown.map(([, l]) => l) };
}

/** A hook link cell past the row's own hook count is not used. */
export const isUnusedHookCell = (row, key) => hookNumber(key) > hooksOf(row);

/** First problem with a row's delivery columns, or null. */
export function directRowProblem(row) {
  if (!sanitizeToken(row.requester_name)) return "Requester is required.";
  if (!(row.ad_type || "").trim()) return "Ad type is required.";
  if (creatorRequired(row.human_ai) && !(row.creator_name || "").trim())
    return "Creator is required for Human ads.";
  if (!(row.editor_name || "").trim()) return "Editor is required.";
  const links = hookLinks(row);
  const missing = links.findIndex((l) => !l);
  if (missing >= 0)
    return `Hook ${missing + 1} link is required (${links.length} hook${links.length > 1 ? "s" : ""}).`;
  const bad = links.findIndex((l) => !validateAssetUrl(l).ok);
  if (bad >= 0)
    return `Hook ${bad + 1} link: ${validateAssetUrl(links[bad]).error}`;
  return null;
}

/** The delivery part of a direct item; the caller spreads the request brief in front. */
export const directDeliveryFields = (row) => ({
  requester_name: row.requester_name,
  ad_type: (row.ad_type || "").trim(),
  creator_name: (row.creator_name || "").trim(),
  editor_name: (row.editor_name || "").trim(),
  hooks_count: hooksOf(row),
  hooks: hookLinks(row).map((url) => ({ ad_asset_url: url })),
});

/** Backend row errors point at submitted items; map them back to grid row numbers. */
export function directErrorMessage(err, gridRowNumbers) {
  const data = err?.response?.data;
  const first = data?.errors?.[0];
  if (first && gridRowNumbers?.[first.index] != null) {
    const more =
      data.errors.length > 1 ? ` (+${data.errors.length - 1} more)` : "";
    return `Row ${gridRowNumbers[first.index]}: ${first.error}${more}`;
  }
  return data?.error_message || err?.message || "Submit failed.";
}
